<template>
  <div
    v-click-outside="hideMultiSelectItems"
    class="multiselect-box"
  >
    <button
      class="multiselect-box__select"
      :disabled="disabled"
      :class="{
        'multiselect-box__select--disabled': disabled,
        'multiselect-box__select--active': selectActive && !showErrorMessage,
        'multiselect-box__select--error': showErrorMessage,
        'multiselect-box__select--none-label': showCountChip ? false : selectedItemsLabel === labelStrings.noneLabel,
        'multiselect-box__select--clear': clear,
      }"
      :style="{
        'max-width': maxWidth ? `${maxWidth}px` : 'auto',
      }"
      type="button"
      @click="handleSelectActive"
    >
      <tooltipped-text :text="selectedItemsLabel" />
      <v-spacer v-if="!clear" />
      <v-chip
        v-if="displayCountChipByDefault || (showCountChip && selectedItems.length)"
        small
        :color="shouldShowCountChipWithoutSelection ? 'transparent' : 'primaryLight'"
        :text-color="shouldShowCountChipWithoutSelection ? 'transparent' : 'primaryWhite'"
        @mouseover="hoverOverChip('over')"
        @mouseleave="hoverOverChip('leave')"
      >
        <span v-if="showRemoveIcon">
          <v-icon
            size="22"
            class="ml-n1"
            color="primaryWhite"
            @click.stop="clearSelection"
          >
            $icon_close
          </v-icon>
        </span>
        <span v-else>{{ selectedItems.length }}</span>
      </v-chip>
      <v-icon
        v-show="!showErrorMessage"
        :size="24"
        class="ml-auto"
        :color="disabled ? 'secondaryLight' : 'secondaryMedium'"
      >
        $icon_arrow_down
      </v-icon>
      <v-icon
        v-show="showErrorMessage"
        :size="24"
        color="negative"
        class="ml-auto"
      >
        $icon_attention
      </v-icon>
      <v-progress-linear
        v-if="searchLoading || loading"
        absolute
        bottom
        indeterminate
        rounded
        height="2"
        :color="loaderColor"
      />
    </button>
    <div
      v-show="selectActive"
      class="multiselect-box__content"
      :style="menuProps?.maxWidth ? { width: menuProps.maxWidth } : {}"
    >
      <div
        v-if="originalItems.length || showSearchBoxOnNoItems"
        class="mb-2"
      >
        <SearchBar
          v-model="currentSearch"
          :collections="[]"
          :icon="searchIcon"
          :show-collection="false"
          :placeholder="labelStrings.searchPlaceholder ? labelStrings.searchPlaceholder : ''"
        />
      </div>
      <div
        ref="multiSelectDropdownRef"
        class="multiselect-box__items"
      >
        <div v-if="!loading && !searchLoading">
          <div
            v-if="originalItems.length && !currentSearch"
            class="multiselect-box__item--normal"
          >
            <CheckBox
              v-model="isSelectedAll"
              :label="labelStrings.allLabel"
              :indeterminate="isIndeterminate"
              class="multiselect-box__item__checkbox"
            />
          </div>
          <div v-if="searchResultItems.length">
            <div
              v-for="item in selectedSearchResultItems"
              :key="`${item.text}${item.value}`"
            >
              <CheckBox
                :label="item.text"
                :value="getSelectedItem(item.value)"
                class="multiselect-box__item__checkbox"
                :label-max-width="labelMaxWidth"
                @change="clickItem(item)"
              />
            </div>
            <v-divider
              v-show="selectedSearchResultItems.length && unselectedSearchResultItems.length"
              class="my-1 mr-1"
            />
            <div
              v-for="item in unselectedSearchResultItems"
              :key="`${item.text}${item.value}`"
            >
              <CheckBox
                :label="item.text"
                :value="getSelectedItem(item.value)"
                class="multiselect-box__item__checkbox"
                :label-max-width="labelMaxWidth"
                @change="clickItem(item)"
              />
            </div>
          </div>
          <div v-if="!currentSearch">
            <div
              v-for="item in selectedItemsList"
              v-show="selectedItemsList.length"
              :key="`${item.text}${item.value}`"
              class="multiselect-box__item--normal"
            >
              <CheckBox
                :label="item.text"
                :value="getSelectedItem(item.value)"
                class="multiselect-box__item__checkbox"
                :label-max-width="labelMaxWidth"
                @change="clickItem(item)"
              />
            </div>
            <v-divider
              v-show="shouldShowDivider"
              class="my-1 mr-1"
            />
          </div>
          <div
            v-if="currentSearch && !isSearchResultOccurred"
            class="multiselect-box__no-results"
          >
            <p>{{ labelStrings.noResultsLabel }}</p>
          </div>
          <div
            v-if="!currentSearch && originalItems.length === 0"
            class="multiselect-box__no-results"
          >
            <p>{{ labelStrings.noItemsLabel }}</p>
          </div>
          <div v-if="!currentSearch">
            <div
              v-for="collection of availableCollections"
              :key="collection.name"
            >
              <p
                v-if="collection.items.length > 0 && collections.length > 1"
                class="multiselect-box__category"
              >
                {{ collection.name }}
              </p>
              <div
                v-for="item in collection.items"
                :key="`${item.text}${item.value}`"
                class="multiselect-box__item--normal"
                @click="clickItem(item)"
              >
                <CheckBox
                  :label="item.text"
                  :value="getSelectedItem(item.value)"
                  class="multiselect-box__item__checkbox"
                  :label-max-width="labelMaxWidth"
                  @change="clickItem(item)"
                />
              </div>
            </div>
          </div>
        </div>
        <loading-spinner
          v-if="
            searchLoading ||
              (!originalItems.length && props.collections.flatMap((collection) => collection.items)?.length)
          "
          is-loading
          :absolute="false"
          class="search-loader"
        />
        <div
          v-if="paginated && !searchLoading"
          data-testid="eewc-multi-select-search-pagination"
          :class="['pagination-wrapper', { 'pagination-wrapper--no-height': paginationRef.completed }]"
        >
          <div
            v-intersect="handleIntersection"
            data-testid="eewc-multi-select-search-intersection"
          />
          <loading-spinner
            v-if="!paginationRef.completed || !paginationRef.loaded"
            is-loading
            :absolute="false"
            :class="['infinite-loader', { 'infinite-loader--margin': !selectedItems.length }]"
          />
        </div>
      </div>
    </div>
    <div
      v-show="showErrorMessage"
      class="multiselect-box__error-message"
    >
      {{ errorMessage || errorBucket[0] }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import _ from 'lodash';

import { IntersectionPaginationOption, IntersectionStateChanger } from '@/utils/types';
import LoadingSpinner from '../../../components/loaders/LoadingSpinner.vue';
import TooltippedText from '../../tooltip/TooltippedText/TooltippedText.vue';
import SearchBar from '../../text-inputs/searchBar/SearchBar.vue';
import CheckBox from '../../selection-controls/CheckBox.vue';

import { Collection, SelectBoxItem, LabelStrings } from '../types';
import type { MenuProps } from '../AutoComplete.vue';
import { clickItemHandler, selectedItemsLabelHandler } from '../multiSelectUtils';

const props = withDefaults(
  defineProps<{
    displayCountChipByDefault?: boolean;
    disabled?: boolean;
    value?: SelectBoxItem[];
    maxWidth?: number;
    collections: Collection[];
    labelStrings: LabelStrings;
    errorMessage?: string;
    labelMaxWidth?: string | number;
    loading?: boolean;
    searchLoading?: boolean;
    paginated?: boolean;
    clear?: boolean;
    searchIcon?: string;
    showCountChip?: boolean;
    showSearchBoxOnNoItems?: boolean;
    rules?: Array<string | boolean | ((value: object) => boolean | string)>;
    menuProps?: MenuProps;
    filterByValue?: boolean;
  }>(),
  {
    value: () => [],
    displayCountChipByDefault: false,
    maxWidth: undefined,
    labelMaxWidth: '100%',
    errorMessage: '',
    loading: false,
    searchLoading: false,
    paginated: false,
    clear: false,
    showCountChip: false,
    showSearchBoxOnNoItems: false,
    searchIcon: '$icon_search',
    filterByValue: false,
  }
);

const emit = defineEmits<{
  (e: 'input', value?: SelectBoxItem[]): void;
  (e: 'click-outside'): void;
  (
    e: 'search',
    value: {
      searchText: string | undefined | null;
      state: IntersectionStateChanger;
    }
  ): void;
  (e: 'infinite', value: IntersectionStateChanger): void;
}>();

const paginationRef = ref<IntersectionPaginationOption>({
  loaded: false,
  completed: false,
  bounceTimer: undefined,
});

const multiSelectDropdownRef = ref<HTMLDivElement | null>(null);

const availableCollections = ref([...props.collections]);
const selectedItemsList = ref([...props.value]);

const loaderColor = computed(() => (props.errorMessage ? 'negative' : 'secondaryMedium'));

const originalItems = computed(() =>
  props.collections.flatMap((collection: Collection) => _.uniqBy(collection.items, 'value'))
);

const inRange = (num: number, a: number, b = 0) => Math.min(a, b) <= num && num < Math.max(a, b);

const isIndeterminate = computed(() => {
  return !!selectedItems?.value.length && inRange(selectedItems?.value?.length, 1, originalItems.value.length);
});

const shouldShowDivider = computed(() => {
  const availableItems = availableCollections.value.flatMap((collection: Collection) => collection.items);

  return selectedItemsList.value.length && availableItems.length;
});

// SELECTED ITEMS
const selectedItems = computed({
  get: () => (props.value ? props.value : []),
  set: (newValue) => {
    emit('input', newValue);
    validate(newValue);
  },
});
const shouldShowCountChipWithoutSelection = computed(
  () => props.displayCountChipByDefault && !selectedItems.value?.length
);

watch(props.value, (newValue) => {
  validate(newValue);
});

// SEARCH
const currentSearchLower = computed(() => currentSearch.value.toLowerCase());

const searchResultItems = computed(() => {
  return searchDropDownItems(currentSearch.value);
});

const selectedSearchResultItems = computed(() =>
  searchResultItems.value.filter((searchedItem) =>
    selectedItems.value.find((selectedItem) => selectedItem.value === searchedItem.value)
  )
);

const unselectedSearchResultItems = computed(() =>
  searchResultItems.value.filter(
    (searchedItem) => !selectedItems.value.find((selectedItem) => selectedItem.value === searchedItem.value)
  )
);

const isSelectedAll = computed({
  get: () => !!originalItems.value.length && props?.value?.length === originalItems.value.length,
  set: (newValue) => {
    if (newValue) {
      selectedItems.value = originalItems.value;
      selectedItemsList.value = originalItems.value.filter(
        (item) => !searchResultItems.value.find((searchedItem) => searchedItem.value === item.value)
      );

      availableCollections.value = props.collections.map((collection: Collection) => {
        const items = collection.items.filter(
          (item) => !originalItems.value.find((selectedItem) => selectedItem.value === item.value)
        );

        return {
          name: collection.name,
          items,
        };
      });
    } else {
      selectedItems.value = [];
      selectedItemsList.value = [];
      availableCollections.value = props.collections.map((collection: Collection) => {
        const items = collection.items.filter(
          (item) => !searchResultItems.value.some((value) => value.value === item.value)
        );

        return {
          name: collection.name,
          items,
        };
      });
    }
  },
});

const isSearchResultOccurred = computed(() => {
  return !!searchDropDownItems(currentSearch.value)?.length;
});

// SELECT ALL
const selectActive = ref<boolean>(false);

const currentSearch = ref<string>('');
// LABEL
const selectedItemsLabel = computed(() =>
  selectedItemsLabelHandler(isSelectedAll.value, selectedItems.value, props.labelStrings, props.showCountChip)
);

const getSelectedItem = (value: string | number) => {
  return selectedItems.value.some((selectedItem) => selectedItem.value === value);
};

// EVENT HANDLERS
const hideMultiSelectItems = () => {
  if (multiSelectDropdownRef.value) multiSelectDropdownRef.value.scrollTop = 0;

  selectActive.value = false;
  emit('click-outside');
};

const handleSelectActive = () => {
  if (selectActive.value && multiSelectDropdownRef.value) {
    multiSelectDropdownRef.value.scrollTop = 0;
  }

  selectActive.value = !selectActive.value;
};

const handleIntersection = (
  entries: IntersectionObserverEntry[],
  observer: IntersectionObserver,
  isIntersecting: boolean
) => {
  if (isIntersecting) {
    if (paginationRef.value.completed) return;
    paginationRef.value.loaded = false;
    emit('infinite', state);
  }
};

// VALIDATION
const isDirty = ref<boolean>(false);
const valid = ref<boolean>(false);
const errorBucket = ref<string[]>([]);
const showRemoveIcon = ref<boolean>(false);

const showErrorMessage = computed(() => {
  if (props.rules?.length) {
    return isDirty.value && !valid.value;
  } else {
    return !!props.errorMessage;
  }
});

function validate(newValue: SelectBoxItem[]) {
  isDirty.value = true;

  if (!props.rules?.length) {
    return;
  }

  const value = newValue || props?.value;
  const errorBucketTemp = [];

  for (let index = 0; index < props.rules.length; index++) {
    const rule = props.rules[index];
    const valid = typeof rule === 'function' ? rule(value) : rule;

    if (valid === false || typeof valid === 'string') {
      errorBucketTemp.push(valid || '');
    }
  }

  errorBucket.value = errorBucketTemp;
  valid.value = errorBucketTemp.length === 0;

  return valid.value;
}

// Function responsible for removing/adding items allFilteredItems
const clickItem = (item: SelectBoxItem) => {
  selectedItems.value = clickItemHandler(item, selectedItems.value);
};

const hoverOverChip = (type: string) => {
  type === 'over' ? (showRemoveIcon.value = true) : (showRemoveIcon.value = false);
};

const clearSelection = () => {
  if (selectedItems.value?.length) {
    selectedItems.value = [];
    currentSearch.value = '';
    hoverOverChip('leave');
  } else if (props.displayCountChipByDefault) {
    selectActive.value = !selectActive.value;
  }
};

const searchDropDownItems = (text: string) => {
  return originalItems.value.filter((item) => {
    const itemName = item.text.toLowerCase();
    const itemValue = item.value.toString().toLowerCase();

    if (text) {
      return itemName.indexOf(text) > -1 || (props.filterByValue ? itemValue.indexOf(text) > -1 : false);
    }
    return false;
  });
};

watch(selectActive, (newValue, oldValue) => {
  if (!newValue && newValue !== oldValue) {
    currentSearch.value = '';

    const items = [...selectedItems.value, ...searchResultItems.value];

    availableCollections.value = props.collections.map((collection: Collection) => ({
      name: collection.name,
      items: collection.items.filter((item) => !items.some((value) => value.value === item.value)),
    }));

    selectedItemsList.value = _.sortBy(selectedItems.value, ['text']);
  }
});

watch(
  () => currentSearchLower.value,
  (newVal) => {
    // debounce for the search
    clearTimeout(paginationRef.value.bounceTimer);
    paginationRef.value.bounceTimer = setTimeout(() => {
      emit('search', { searchText: newVal, state });
    }, 500);
  }
);

watch(
  () => props.value,
  (newValue) => {
    if (!newValue.length) {
      availableCollections.value = props.collections.map((collection: Collection) => {
        const items = collection.items.filter(
          (item) => !searchResultItems.value.some((value) => value.value === item.value)
        );

        return {
          name: collection.name,
          items,
        };
      });

      selectedItemsList.value = [];
    }
  }
);

watch(
  () => props.collections,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      selectedItemsList.value = originalItems.value.filter(
        (item) => !searchResultItems.value.find((searchedItem) => searchedItem.value === item.value)
      );

      availableCollections.value = newValue.map((collection: Collection) => ({
        name: collection.name,
        items: collection.items.filter((item) => !selectedItemsList.value.some((value) => value.value === item.value)),
      }));
    }
  },
  {
    deep: true,
  }
);

const state: IntersectionStateChanger = {
  loaded: () => {
    paginationRef.value.loaded = true;
  },
  completed: () => {
    paginationRef.value.completed = true;
    paginationRef.value.loaded = true;
  },
  reset: () => {
    paginationRef.value.loaded = false;
    paginationRef.value.completed = false;
  },
};

onMounted(() => {
  const items = [...selectedItems.value, ...searchResultItems.value];

  availableCollections.value = props.collections.map((collection: Collection) => ({
    name: collection.name,
    items: collection.items.filter((item) => !items.some((value) => value.value === item.value)),
  }));
});

defineExpose({ validate });
</script>

<style lang="scss" scoped>
@import '../../../assets/styles/main';
@import '../multiSelectStyles';

.multiselect-box {
  &__select {
    position: relative;

    &--clear {
      border: none;
      background: none;
      box-shadow: none !important;
    }
  }

  &__item {
    height: 28px;
    &__checkbox {
      padding-top: 2px !important;
    }
    &--normal {
      &:hover {
        background: $accentClear;
        border-radius: 4px;
        margin-right: 4px;
      }
    }
    &--searched {
      background: $accentClear;
      border-radius: 4px;
      margin-right: 4px;
    }
  }
  &__searched-items {
    background: $accentClear;
    box-shadow: none;
    border-radius: 4px;
  }

  &__category {
    @include body-3;
    color: $secondaryMedium;
    margin: 4px 0 !important;
  }

  &__no-results {
    display: flex;
    align-items: center;
    height: 44px;
    p {
      @include body-2;
      margin: 0 !important;
    }
  }
}

.pagination-wrapper {
  height: 60px;
  display: grid;
  place-items: center;

  &--no-height {
    height: auto;
  }

  .infinite-loader {
    text-align: center;

    &--margin {
      margin-top: -30px;
    }
  }
}

.search-loader {
  height: 60px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.v-chip {
  @include subtitle-3;
  margin-left: 12px;
  margin-right: 10px;
  max-width: 37px;
  min-width: 37px;
  display: flex;
  justify-content: center;
  padding: 0 !important;
}
</style>
